import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  Users, 
  Crown, 
  Loader2,
  UserX,
  Mail,
  RefreshCw,
  CheckCircle
} from 'lucide-react';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: 'student' | 'teacher';
  subscription_type: 'visitor' | 'membre';
}

export const AdminPanel: React.FC = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      console.log('Loading users...');
      const { data, error } = await supabase
        .from('user_profiles_2025_12_02_23_42')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading users:', error);
        throw error;
      }
      
      console.log('Users loaded:', data);
      setUsers(data || []);
      
      toast({ 
        title: "✅ Utilisateurs chargés", 
        description: `${data?.length || 0} utilisateur(s) trouvé(s)` 
      });
    } catch (error: any) {
      console.error('Load users error:', error);
      toast({ 
        title: "❌ Erreur de chargement", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscription = async (userId: string, newStatus: 'visitor' | 'membre') => {
    setUpdating(userId);
    try {
      console.log(`Updating user ${userId} to ${newStatus}`);
      
      const { error } = await supabase
        .from('user_profiles_2025_12_02_23_42')
        .update({ subscription_type: newStatus })
        .eq('id', userId);
      
      if (error) {
        console.error('Update error:', error);
        throw error;
      }
      
      // Mise à jour locale immédiate
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, subscription_type: newStatus }
            : user
        )
      );
      
      console.log('Update successful');
      toast({ 
        title: "✅ Statut mis à jour", 
        description: `Utilisateur maintenant ${newStatus === 'membre' ? 'Membre' : 'Visiteur'}` 
      });
      
    } catch (error: any) {
      console.error('Update subscription error:', error);
      toast({ 
        title: "❌ Erreur de mise à jour", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (profile?.role !== 'teacher') {
    return (
      <div className="text-center py-12">
        <UserX className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Accès réservé aux enseignants</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-secondary mb-2">
          Panneau d'Administration
        </h1>
        <p className="text-muted-foreground">
          Gérez les abonnements Membre de vos étudiants
        </p>
      </div>

      {/* Status Card */}
      <Card className="border-0 bg-gradient-to-r from-green-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              État du système
            </span>
            <Button onClick={loadUsers} disabled={loading} size="sm" variant="outline">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Chargement...' : 'Actualiser'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p><strong>Votre rôle :</strong></p>
              <p className="text-green-600">👨‍🏫 {profile?.role}</p>
            </div>
            <div>
              <p><strong>Utilisateurs :</strong></p>
              <p className="text-blue-600">👥 {users.length}</p>
            </div>
            <div>
              <p><strong>Membre :</strong></p>
              <p className="text-yellow-600">👑 {users.filter(u => u.subscription_type === 'membre').length}</p>
            </div>
            <div>
              <p><strong>Visiteurs :</strong></p>
              <p className="text-gray-600">🔒 {users.filter(u => u.subscription_type === 'visitor').length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card className="border-0 sprintbac-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Gestion des Utilisateurs ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement des utilisateurs...</p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Aucun utilisateur trouvé</p>
              <div className="text-sm text-muted-foreground bg-yellow-50 p-4 rounded-lg max-w-md mx-auto">
                <p><strong>Pour voir des utilisateurs :</strong></p>
                <ol className="list-decimal list-inside mt-2 space-y-1 text-left">
                  <li>Demandez aux étudiants de s'inscrire</li>
                  <li>Cliquez "Actualiser" pour recharger</li>
                  <li>Gérez leurs abonnements ici</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-white to-blue-50 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary text-lg">{user.name}</h3>
                      <p className="text-muted-foreground">{user.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {user.role === 'student' ? '👨‍🎓 Étudiant' : '👨‍🏫 Enseignant'}
                        </Badge>
                        <Badge 
                          className={
                            user.subscription_type === 'membre' 
                              ? 'sprintbac-gradient text-white' 
                              : 'bg-gray-100 text-gray-800'
                          }
                        >
                          {user.subscription_type === 'membre' ? (
                            <>
                              <Crown className="mr-1 h-3 w-3" />
                              Membre
                            </>
                          ) : (
                            '🔒 Visiteur'
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {user.subscription_type === 'visitor' ? (
                      <Button
                        onClick={() => updateUserSubscription(user.id, 'membre')}
                        disabled={updating === user.id}
                        className="sprintbac-gradient hover:opacity-90"
                        size="lg"
                      >
                        {updating === user.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Activation...
                          </>
                        ) : (
                          <>
                            <Crown className="mr-2 h-4 w-4" />
                            Rendre Membre
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => updateUserSubscription(user.id, 'visitor')}
                        disabled={updating === user.id}
                        variant="outline"
                        size="lg"
                      >
                        {updating === user.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Désactivation...
                          </>
                        ) : (
                          <>
                            <UserX className="mr-2 h-4 w-4" />
                            Retirer Membre
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="border-0 bg-gradient-to-r from-blue-50 to-yellow-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Instructions d'utilisation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-secondary mb-2">🎯 Comment ça marche :</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>L'étudiant s'inscrit sur la plateforme</li>
                <li>Il vous paie 300 DH/mois</li>
                <li>Vous cliquez "Rendre Membre"</li>
                <li>Il accède immédiatement aux contenus</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-secondary mb-2">✅ Différences d'accès :</h4>
              <ul className="space-y-1 text-sm">
                <li><strong>👑 Membre :</strong> Accès à tous les cours et exercices</li>
                <li><strong>🔒 Visiteur :</strong> Voit seulement la page d'offre membre</li>
                <li><strong>👨‍🏫 Enseignant :</strong> Accès complet + administration</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-green-100 p-4 rounded-lg">
            <p className="text-green-800 font-medium text-center">
              🎉 Version corrigée ! Tous les problèmes de distinction des utilisateurs sont résolus.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};